import {
  Button,
  Dialog,
  Input,
  Kbd,
  detectPlatform,
  resolveShortcut,
  useShortcutPreferences,
  validateShortcut,
  type ShortcutDefinition,
} from "@z/ui";
import { useState } from "react";

function ShortcutEditor({
  definition,
  onClose,
}: {
  definition: ShortcutDefinition;
  onClose: () => void;
}) {
  const preferences = useShortcutPreferences();
  const [value, setValue] = useState(resolveShortcut(preferences, definition.id) ?? "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const result = value.trim()
    ? validateShortcut(definition.id, value, preferences, detectPlatform())
    : {};
  const save = async (binding: string | null | undefined) => {
    if (saving) return;
    const next = { ...preferences.overrides };
    if (binding === undefined) delete next[definition.id];
    else next[definition.id] = binding;
    setSaving(true);
    setSaveError(undefined);
    try {
      await preferences.save(next);
      onClose();
    } catch {
      setSaveError("Could not save this shortcut. Try again.");
      setSaving(false);
    }
  };
  const resetResult = definition.defaultShortcut
    ? validateShortcut(definition.id, definition.defaultShortcut, preferences, detectPlatform())
    : {};
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>{definition.title} shortcut</Dialog.Title>
          <Dialog.Description>
            Type a binding, such as n t or mod+alt+e. Mod means Cmd on Mac and Ctrl elsewhere. Leave
            blank to disable.
          </Dialog.Description>
        </Dialog.Header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!result.error) void save(result.shortcut ?? null);
          }}
          className="flex flex-col gap-3"
        >
          <Input
            aria-label="Shortcut binding"
            aria-describedby="shortcut-feedback"
            aria-invalid={Boolean(result.error)}
            autoComplete="off"
            spellCheck={false}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={saving}
          />
          {result.shortcut ? (
            <div className="flex items-center gap-2 text-sm text-fg-muted">
              Preview <Kbd keys={result.shortcut} />
            </div>
          ) : null}
          <p
            id="shortcut-feedback"
            role={result.error || saveError ? "alert" : "status"}
            className={`text-sm ${result.error || saveError ? "text-danger" : "text-fg-muted"}`}
          >
            {saveError ??
              result.error ??
              result.warning ??
              (definition.scope === "editor"
                ? "Runs only in the editor. Use a single Cmd or Ctrl combination."
                : definition.allowInEditor
                  ? "Modifier shortcuts also work while typing. Letter sequences run only outside text fields."
                  : "Runs outside text fields so writing stays uninterrupted.")}
          </p>
          <Dialog.Footer>
            <Button
              type="button"
              variant="ghost"
              disabled={saving || Boolean(resetResult.error)}
              onClick={() => void save(undefined)}
              title={resetResult.error}
            >
              Restore default
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || Boolean(result.error)}>
              {saving ? "Saving…" : "Save shortcut"}
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function KeyboardSettings() {
  const preferences = useShortcutPreferences();
  const [editing, setEditing] = useState<ShortcutDefinition | null>(null);
  const [query, setQuery] = useState("");
  const groups = [...new Set(preferences.definitions.map((item) => item.group))];
  const matches = preferences.definitions.filter((item) =>
    `${item.title} ${item.group}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <section className="mb-6" aria-labelledby="keyboard-shortcuts-title">
      <h2 id="keyboard-shortcuts-title" className="mb-2 text-sm font-medium">
        Keyboard shortcuts
      </h2>
      <p className="mb-3 text-sm text-fg-muted">
        Press sequence keys one after another, within one second. Sequences pause while typing.
        Changes apply only on this device.
      </p>
      <Input
        aria-label="Find shortcut"
        placeholder="Find a shortcut…"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {groups.map((group) => {
        const definitions = matches.filter((item) => item.group === group);
        return definitions.length ? (
          <div key={group} className="mt-4">
            <h3 className="mb-1 text-xs font-medium text-fg-muted">{group}</h3>
            {definitions.map((definition) => {
              const shortcut = resolveShortcut(preferences, definition.id);
              return (
                <div
                  key={definition.id}
                  className="flex items-center justify-between gap-3 border-b border-border/60 py-2"
                >
                  <span className="text-sm">{definition.title}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    aria-label={`Change shortcut: ${definition.title}`}
                    onClick={() => setEditing(definition)}
                  >
                    {shortcut ? (
                      <Kbd keys={shortcut} />
                    ) : (
                      <span className="text-xs text-fg-muted">Unassigned</span>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        ) : null;
      })}
      {!matches.length ? (
        <p className="py-3 text-sm text-fg-muted">No matching shortcuts.</p>
      ) : null}
      {editing ? (
        <ShortcutEditor key={editing.id} definition={editing} onClose={() => setEditing(null)} />
      ) : null}
    </section>
  );
}
